import React, { useEffect, useState } from 'react';
import { IconX, IconLightbulb, IconChevronDown } from './Icons';

const IconParagraph = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden><path d="M2 2.5a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm0 3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"/></svg>;
const IconBuilding = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden><path d="M14.763.075A.5.5 0 0 1 15 .5v15a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5V14h-1v1.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V10a.5.5 0 0 1 .342-.474L6 7.64V4.5a.5.5 0 0 1 .276-.447l8-4a.5.5 0 0 1 .487.022zM6 8.694 1 10.36V15h5V8.694zM7 15h2v-1.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5V15h2V1.309l-7 3.5V15z"/></svg>;
const IconLink = () => <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden><path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 0 1-2.83-2.83l.793-.792a4 4 0 0 1-.128-1.287z"/><path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 0 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 0 0-4.243-4.243L8.586 5.5z"/></svg>;

function normalizeEndpointUrl(value) {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const candidate = normalizeEndpointUrl(item);
      if (candidate) return candidate;
    }
    return null;
  }

  if (value && typeof value === 'object') {
    return (
      normalizeEndpointUrl(value.url) ||
      normalizeEndpointUrl(value.href) ||
      normalizeEndpointUrl(value.value) ||
      null
    );
  }

  return null;
}

function normalizeEndpointName(value) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeSubmodelVersions(raw) {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw
      .map((entry) => {
        if (typeof entry === 'string') return entry.trim();
        if (entry && typeof entry === 'object') {
          const name = String(entry.name || entry.submodel || '').trim();
          const version = String(entry.version || '').trim();
          if (name && version) return `${name} v${version}`;
          if (name) return name;
        }
        return '';
      })
      .filter(Boolean);
  }

  if (raw && typeof raw === 'object') {
    return Object.entries(raw)
      .map(([name, version]) => {
        const cleanName = String(name || '').trim();
        const cleanVersion = String(version || '').trim();
        if (!cleanName) return '';
        return cleanVersion ? `${cleanName} v${cleanVersion}` : cleanName;
      })
      .filter(Boolean);
  }

  return [];
}

function DetailModal({ descriptor, onClose }) {
  const [openSection, setOpenSection] = useState('description');

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!descriptor) return null;

  const companyData = descriptor.company_data || {};
  const companyName = companyData.company_name || descriptor.company_name || 'Unknown Company';
  const endpoints = descriptor.infrastructure_endpoints || {};
  const capabilities = descriptor.capabilities || {};

  const toggle = (key) => {
    setOpenSection((prev) => (prev === key ? null : key));
  };

  const endpointEntries = Object.entries(endpoints)
    .map(([name, value]) => {
      const endpointUrl = normalizeEndpointUrl(value);
      if (!endpointUrl) return null;
      const endpointName = normalizeEndpointName(name) || 'Endpoint';
      return { name: endpointName, url: endpointUrl };
    })
    .filter(Boolean);

  const endpointList = [];
  const seenEndpointKeys = new Set();
  endpointEntries.forEach((entry) => {
    const endpointKey = `${entry.name}|||${entry.url}`;
    if (seenEndpointKeys.has(endpointKey)) return;
    seenEndpointKeys.add(endpointKey);
    endpointList.push(entry);
  });

  const submodelVersionItems = normalizeSubmodelVersions(capabilities.submodel_versions);

  const hasCompany = companyData && Object.values(companyData).some((v) => v !== undefined && v !== null && v !== '');
  const hasCapabilities = capabilities && (
    capabilities.capability_description ||
    capabilities.aas_metamodel_version ||
    (capabilities.keywords && capabilities.keywords.length) ||
    (capabilities.submodels_supported && capabilities.submodels_supported.length) ||
    submodelVersionItems.length
  );

  return (
    <div
      className="ds-detail-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ds-detail-modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="ds-detail-modal">
        <div className="ds-detail-modal__header">
          <h2 id="ds-detail-modal-title" className="ds-detail-modal__title">
            {companyName}
            {Boolean(descriptor.demo) && (
              <span className="ds-demo-badge" title="Demo environment - not a production service">
                Demo
              </span>
            )}
          </h2>
          <button
            type="button"
            className="ds-detail-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <IconX aria-hidden />
          </button>
        </div>
        <div className="ds-detail-modal__body">
          {companyData.company_description && (
            <div className={`ds-detail-dropdown ${openSection === 'description' ? 'ds-detail-dropdown--open' : ''}`}>
              <button type="button" className="ds-detail-dropdown__head" onClick={() => toggle('description')}>
                <span><IconParagraph /> Description</span>
                <IconChevronDown className="ds-detail-chevron" aria-hidden />
              </button>
              <div className="ds-detail-dropdown__body">
                <div className="ds-detail-dropdown__inner">
                  <p className="ds-detail-modal__section-body" style={{ margin: 0 }}>{companyData.company_description}</p>
                </div>
              </div>
            </div>
          )}

          {(descriptor.domain || hasCompany) && (
            <div className={`ds-detail-dropdown ${openSection === 'company' ? 'ds-detail-dropdown--open' : ''}`}>
              <button type="button" className="ds-detail-dropdown__head" onClick={() => toggle('company')}>
                <span><IconBuilding /> Company Information</span>
                <IconChevronDown className="ds-detail-chevron" aria-hidden />
              </button>
              <div className="ds-detail-dropdown__body">
                <div className="ds-detail-dropdown__inner">
                  {descriptor.domain && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Domain</span>
                      <span className="ds-detail-dropdown__value">
                        <a href={descriptor.domain.startsWith('http') ? descriptor.domain : `https://${descriptor.domain}`} target="_blank" rel="noopener noreferrer">{descriptor.domain}</a>
                      </span>
                    </div>
                  )}
                  {companyData.homepage_url && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Homepage</span>
                      <span className="ds-detail-dropdown__value">
                        <a href={companyData.homepage_url} target="_blank" rel="noopener noreferrer">{companyData.homepage_url}</a>
                      </span>
                    </div>
                  )}
                  {companyData.headquarters && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Headquarters</span>
                      <span className="ds-detail-dropdown__value">{companyData.headquarters}</span>
                    </div>
                  )}
                  {companyData.founding_year && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Founding Year</span>
                      <span className="ds-detail-dropdown__value">{companyData.founding_year}</span>
                    </div>
                  )}
                  {companyData.industries && companyData.industries.length > 0 && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Industries</span>
                      <span className="ds-detail-dropdown__value">{companyData.industries.join(', ')}</span>
                    </div>
                  )}
                  {companyData.main_product_groups && companyData.main_product_groups.length > 0 && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Main Product Groups</span>
                      <span className="ds-detail-dropdown__value">{companyData.main_product_groups.join(', ')}</span>
                    </div>
                  )}
                  {companyData.vat_number && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">VAT Number</span>
                      <span className="ds-detail-dropdown__value">{companyData.vat_number}</span>
                    </div>
                  )}
                  {companyData.commercial_register_number && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Commercial Register</span>
                      <span className="ds-detail-dropdown__value">{companyData.commercial_register_number}</span>
                    </div>
                  )}
                  {companyData.order_currency && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Order Currency</span>
                      <span className="ds-detail-dropdown__value">{typeof companyData.order_currency === 'object' ? [companyData.order_currency.en, companyData.order_currency.de].filter(Boolean).join(' / ') : companyData.order_currency}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {endpointList.length > 0 && (
            <div className={`ds-detail-dropdown ${openSection === 'endpoints' ? 'ds-detail-dropdown--open' : ''}`}>
              <button type="button" className="ds-detail-dropdown__head" onClick={() => toggle('endpoints')}>
                <span><IconLink /> Endpoints ({endpointList.length})</span>
                <IconChevronDown className="ds-detail-chevron" aria-hidden />
              </button>
              <div className="ds-detail-dropdown__body">
                <div className="ds-detail-dropdown__inner">
                  {endpointList.map(({ name, url }) => {
                    return (
                      <div className="ds-detail-dropdown__row ds-detail-dropdown__row--endpoint" key={`${name}-${url}`}>
                        <span className="ds-detail-dropdown__label">{name}</span>
                        <span className="ds-detail-dropdown__value">
                          <a href={url} target="_blank" rel="noopener noreferrer">
                            {url}
                          </a>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {hasCapabilities && (
            <div className={`ds-detail-dropdown ${openSection === 'capabilities' ? 'ds-detail-dropdown--open' : ''}`}>
              <button type="button" className="ds-detail-dropdown__head" onClick={() => toggle('capabilities')}>
                <span><IconLightbulb aria-hidden /> Capabilities &amp; Keywords</span>
                <IconChevronDown className="ds-detail-chevron" aria-hidden />
              </button>
              <div className="ds-detail-dropdown__body">
                <div className="ds-detail-dropdown__inner">
                  {capabilities.capability_description && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Description</span>
                      <span className="ds-detail-dropdown__value">{capabilities.capability_description}</span>
                    </div>
                  )}
                  {capabilities.language && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Language</span>
                      <span className="ds-detail-dropdown__value">{capabilities.language}</span>
                    </div>
                  )}
                  {capabilities.asset_kind && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Asset Kind</span>
                      <span className="ds-detail-dropdown__value">{capabilities.asset_kind}</span>
                    </div>
                  )}
                  {capabilities.aas_metamodel_version && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">AAS Metamodel Version</span>
                      <span className="ds-detail-dropdown__value">{capabilities.aas_metamodel_version}</span>
                    </div>
                  )}
                  {capabilities.keywords && capabilities.keywords.length > 0 && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Keywords</span>
                      <span className="ds-detail-dropdown__value">{capabilities.keywords.join(', ')}</span>
                    </div>
                  )}
                  {capabilities.submodels_supported && capabilities.submodels_supported.length > 0 && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Submodels Supported</span>
                      <span className="ds-detail-dropdown__value">{capabilities.submodels_supported.join(', ')}</span>
                    </div>
                  )}
                  {submodelVersionItems.length > 0 && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Submodel Versions</span>
                      <span className="ds-detail-dropdown__value">{submodelVersionItems.join(', ')}</span>
                    </div>
                  )}
                  {capabilities.capability_types && capabilities.capability_types.length > 0 && (
                    <div className="ds-detail-dropdown__row">
                      <span className="ds-detail-dropdown__label">Capability Types</span>
                      <span className="ds-detail-dropdown__value">{capabilities.capability_types.join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DetailModal;

